import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { cn } from '@/lib/cn';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useApplyTeamThemeVars, useFavoriteTeamTheme } from '@/hooks/useFavoriteTeamTheme';
import { StandingsSidebar } from '@/components/StandingsSidebar';
import { FavoriteTeamGate } from '@/components/FavoriteTeamGate';
import { PendingRequestsDot } from '@/components/GroupsCard';
import { GlobalBackdrop } from '@/components/GlobalBackdrop';
import { MainColumnFlag } from '@/components/MainColumnFlag';
import { TeamBackdrop } from '@/components/TeamBackdrop';
import { Spinner } from '@/components/Spinner';
import { useFontsReady } from '@/lib/font-loading';

interface NavItem {
  to: string;
  label: string;
  mobileLabel?: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
  authOnly?: boolean;
}

function HomeIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3.5 9.5L10 4L16.5 9.5V16.2C16.5 16.64 16.14 17 15.7 17H12.5V12.5H7.5V17H4.3C3.86 17 3.5 16.64 3.5 16.2V9.5Z" />
    </svg>
  );
}

function TeamsIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 17V3.5" />
      <path d="M5 4.2C8 3.3 10 5.1 13 4.2C14.2 3.84 15 3.9 15.5 4.2V10.8C15 10.5 14.2 10.44 13 10.8C10 11.7 8 9.9 5 10.8" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6V10L12.5 12" />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="10" cy="7.5" r="3" />
      <path d="M3.8 16.5C4.6 13.6 7 12 10 12C13 12 15.4 13.6 16.2 16.5" />
    </svg>
  );
}

// Arena bowl seen from the side — elliptical rim + tapered walls. Emoji
// (🏟) is not an option here: iOS renders it full-color, ignoring CSS
// `color`, so it clashes with the monochrome stroke icons in the tab bar.
function StadiumIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <ellipse cx="10" cy="7" rx="7" ry="2.4" />
      <path d="M3 7L4.8 15C5.6 16 14.4 16 15.2 15L17 7" />
    </svg>
  );
}

// Primary nav items appear in BOTH the desktop sidebar and the mobile
// bottom tab bar (the tab bar also picks up non-admin secondary items —
// see visibleMobile). Keep the tab count ≤6 — more cramps small phones.
const primaryNavItems: NavItem[] = [
  { to: '/', label: 'Home', icon: <HomeIcon /> },
  { to: '/schedule', label: 'Fixtures', icon: <ClockIcon /> },
  { to: '/standings', label: 'Standings', icon: '☰', authOnly: true },
  { to: '/teams', label: 'Teams', icon: <TeamsIcon /> },
  { to: '/stadiums', label: 'Stadiums', icon: <StadiumIcon /> },
];

const secondaryNavItems: NavItem[] = [
  { to: '/profile', label: 'Profile', mobileLabel: 'You', icon: <ProfileIcon />, authOnly: true },
  { to: '/admin/matches', label: 'Admin', icon: '⚙', adminOnly: true },
];

function isVisible(item: NavItem, user: ReturnType<typeof useCurrentUser>): boolean {
  if (item.adminOnly && user?.role !== 'admin') return false;
  if (item.authOnly && !user) return false;
  return true;
}

export function App() {
  const user = useCurrentUser();
  const { pathname } = useLocation();
  // Apply the user's favorite team colors / font to <html> whenever it
  // resolves. Null (no favorite) → removes overrides and :root defaults
  // take over, so the sidebar/tabbar accents fade to zero opacity.
  const { theme: favoriteTheme, isLoading: themeLoading } = useFavoriteTeamTheme();
  useApplyTeamThemeVars(favoriteTheme);
  // Block on the base UI fonts AND the active team font so the first
  // painted frame uses final glyphs — without this the headlines briefly
  // render in the system fallback before the Google Fonts CSS loads,
  // causing a visible swap-in flicker.
  const fontsReady = useFontsReady(favoriteTheme?.font ?? null);

  // Hold every route behind a loader until the auth check has resolved,
  // the favorite team's theme has resolved (if any), AND the fonts the
  // first paint will use are loaded. Without this gate, refreshing the
  // page paints the app under the neutral fallback palette / system font
  // for one frame before the takeover applies. The Spinner uses neutral
  // tokens and no text, so it doesn't itself flash.
  if (user === undefined || themeLoading || !fontsReady) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg)',
        }}
      >
        <Spinner size="lg" />
      </div>
    );
  }

  // Auth / invite pages render bare — no sidebar, no group selector, no tabs.
  // The accept-invite page must be accessible to both logged-in users (so
  // they can accept) and logged-out users (so they can log in / sign up),
  // and it manages its own chrome.
  const bare =
    pathname === '/login' ||
    pathname === '/signup' ||
    pathname === '/verify-email' ||
    pathname === '/reroll' ||
    pathname.startsWith('/accept_group_invite/') ||
    pathname.startsWith('/join/');
  if (bare) return <Outlet />;

  // Admins need to reach /admin/* even without a favorite team — the seed
  // page lives there, and the gate itself is unusable until teams exist.
  const adminBypass = user?.role === 'admin' && pathname.startsWith('/admin');
  if (user && !user.favoriteTeamId && !adminBypass) return <FavoriteTeamGate />;

  const visiblePrimary = primaryNavItems.filter((i) => isVisible(i, user));
  const visibleSecondary = secondaryNavItems.filter((i) => isVisible(i, user));
  // The bottom tab bar is the ONLY nav under 820px (the sidebar is hidden),
  // so non-admin secondary items (Profile) join it there. Profile stays
  // last; Admin stays desktop-only.
  const visibleMobile = [
    ...visiblePrimary.filter((i) => i.to !== '/profile'),
    ...visibleSecondary.filter((i) => !i.adminOnly),
    ...visiblePrimary.filter((i) => i.to === '/profile'),
  ];

  const initials =
    user?.displayName
      ?.split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]!.toUpperCase())
      .join('') ?? 'YU';

  return (
    <>
      <GlobalBackdrop theme={favoriteTheme} />
      <div className="shell">
      <aside className="sidebar">
        <Link to="/" state={{ navRoot: true }} className="brand">
          <div className="brand-mark">
            <span>M</span>
          </div>
          <div className="brand-word">
            <div className="t1">MONDY</div>
            <div className="t2">World Cup 2026</div>
          </div>
        </Link>

        {visiblePrimary.map((item) => (
          <SidebarLink key={item.to} item={item} />
        ))}

        {visibleSecondary.length > 0 ? (
          <>
            <div
              style={{
                margin: '8px 6px',
                borderTop: '1px solid var(--line)',
              }}
            />
            {visibleSecondary.map((item) => (
              <SidebarLink key={item.to} item={item} />
            ))}
          </>
        ) : null}

        {/* Greeting pill — sits directly after the nav routes so the
            empty space falls below it and the user footer pins to the
            absolute bottom (per the reference image). */}
        {favoriteTheme ? (
          <div className="sidebar-greeting">
            <div className="k">From the terraces</div>
            <div className="g">{favoriteTheme.greeting}</div>
          </div>
        ) : null}

        {/* Bottom-pinned user footer. `margin-top: auto` on this single
            container keeps the profile card glued to the bottom of the
            sidebar, leaving empty space between the greeting pill and
            the footer. */}
        {user === undefined ? null : (
          <div className="sidebar-bottom">
            {user ? (
              <Link to="/profile" state={{ navRoot: true }} className="sidebar-footer">
                <div className="avatar">{initials}</div>
                <div className="who">
                  <b>{user.displayName}</b>
                  <span>{user.email}</span>
                </div>
              </Link>
            ) : (
              <Link to="/login" state={{ navRoot: true }} className="btn btn-ghost">
                Log in
              </Link>
            )}
          </div>
        )}
      </aside>

      <main className="app-main">
        {favoriteTheme ? (
          <div className="main-pane-backdrop" aria-hidden="true">
            <TeamBackdrop theme={favoriteTheme} intensity={0.55} />
          </div>
        ) : null}
        {favoriteTheme ? <MainColumnFlag theme={favoriteTheme} /> : null}
        <Outlet />
      </main>

      {/* Right rail — desktop only, hidden under 1180px via .shell rules.
          Logged-out visitors don't get standings, so the rail is omitted. */}
      {user ? <StandingsSidebar /> : null}

      {/* Mobile bottom tabs */}
      <nav className="mobile-tabs">
        {visibleMobile.map((item) => {
          const isProfile = item.to === '/profile';
          return (
            <NavLink
              key={item.to}
              to={item.to}
              state={{ navRoot: true }}
              end={item.to === '/'}
              className={({ isActive }) => cn(isActive && 'active')}
            >
              <span
                aria-hidden
                style={{
                  fontSize: 18,
                  lineHeight: 1,
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                {item.icon}
                {isProfile ? <PendingRequestsDot /> : null}
              </span>
              <span>{item.mobileLabel ?? item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      </div>
    </>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  const isProfile = item.to === '/profile';
  return (
    <NavLink
      to={item.to}
      state={{ navRoot: true }}
      end={item.to === '/'}
      className={({ isActive }) => cn('nav-link', isActive && 'active')}
    >
      <span className="ico" style={{ position: 'relative' }} aria-hidden>
        {item.icon}
        {isProfile ? <PendingRequestsDot /> : null}
      </span>
      <span>{item.label}</span>
    </NavLink>
  );
}
